import {
  type PlayerRatingAllResponse,
  type PlayerTransfer,
  type RelatedTransferResponse,
} from '@on3/api';
import { On3IconAlert } from '@on3/icons';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { StarRating } from '@on3/ui-lib/components/Player/StarRating';
import { StarRanking } from '@on3/ui-lib/components/Player/StarRating/Ranking';
import {
  ServiceLogo,
  type ServiceType,
} from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { useAuth } from '@on3/ui-lib/index';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { createUrl } from '@on3/ui-lib/utils/url';
import { type ColumnOption } from '@public/components/Db/Filters/SortFilter';
import { PremiumBlock } from '@public/components/Db/Profile/PremiumBlock/PremiumBlock';
import { TransferStatus } from '@public/components/RecruitmentStatus/TransferStatus';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import styles from './IndustryComparisonItem.module.scss';

interface IndustryComparisonPlayerItemProps {
  className?: string;
  columns: ColumnOption[];
  selected?: ColumnOption;
  player: PlayerTransfer;
  listIndex: number;
  relatedModel?: RelatedTransferResponse;
}
const defaultRatings = [
  { type: 'Industry' },
  { type: 'On3' },
  { type: '247' },
  { type: 'Rivals' },
] as PlayerRatingAllResponse[];

export const IndustryComparisonTpItem = ({
  className,
  columns,
  selected,
  player,
  listIndex,
  relatedModel,
}: IndustryComparisonPlayerItemProps) => {
  const router = useRouter();
  const { user } = useAuth();
  const { year = relatedModel?.year } = router.query;
  const { transferIndustryComparison, commitStatus: status } = player || {};
  const ratings = transferIndustryComparison?.filter(
    (r) => r.type !== 'Consensus' && r.type !== 'Espn',
  );

  const showRatings = listIndex < 5 || user?.has;
  const isRivalsIndustry = year && +year > 2024;
  const industryRating = ratings?.[0]?.rating?.toFixed(2);
  const playerRatings = ratings?.length ? ratings : defaultRatings;
  const showRank = columns.includes('portalRank');
  const playerStatus = status?.shortTermSignee
    ? 'short-term'
    : status?.transferredAsset
      ? 'transfer'
      : slugify(status?.type) || 'none';
  const playerRank = showRank
    ? player.transferRating?.consensusNationalRank
    : '-';
  const utm_campaign =
    relatedModel?.type === 'wire'
      ? 'industry-comparison-wire'
      : 'industry-comparison-top';

  return (
    <li
      className={clsx(styles.block, className)}
      data-rank={showRank}
      data-rivals-industry={isRivalsIndustry}
      data-status={playerStatus}
      role="row"
    >
      {showRank && (
        <span className={styles.rank} role="cell">
          {playerRank}
        </span>
      )}

      <figure className={styles.avatar} role="cell">
        <Avatar
          altText={`${player.name} Avatar`}
          domain={player.defaultAsset?.domain}
          fit="cover"
          height={100}
          quality={95}
          source={player.defaultAsset?.source}
          type={player.defaultAsset?.type}
          width-={100}
        />
      </figure>
      <div className={styles.details} role="cell">
        <div className={styles.container}>
          <Link
            aria-labelledby="player-name"
            className={styles.name}
            href={createUrl(['rivals', player.slug])}
            useSiteColor
          >
            {player.name}
          </Link>

          {status?.shortTermSignee && <On3IconAlert />}
        </div>
        <dl className={styles.vitalsContainer}>
          {player.classYear && (
            <>
              <dt>High School Class Year</dt>
              <dd>H.S. {player.classYear}</dd>
            </>
          )}
          {player.positionAbbreviation && (
            <>
              <dt>Position Abbreviation</dt>
              <dd>{player.positionAbbreviation}</dd>
            </>
          )}

          {player.height && (
            <>
              <dt>Height</dt>
              <dd>{player.height}</dd>
            </>
          )}
          {!!player.weight && (
            <>
              <dt>Weight</dt>
              <dd>{player.weight}</dd>
            </>
          )}
          {!!player.nilValue && (
            <>
              <dt data-type="nil">NIL Value:</dt>
              <dd data-type="nil">{formatCurrency(player.nilValue)}</dd>
            </>
          )}
        </dl>
        <dl className={styles.homeContainer}>
          {(player.highSchoolName || player.homeTownName) && (
            <>
              <dt>High School / Hometown</dt>
              <dd className={styles.highschoolHometown}>
                <Link
                  className={styles.highschool}
                  href={createUrl(['high-school', player.highSchool?.urlSlug])}
                >
                  {player.highSchoolName}
                </Link>
                {player.homeTownName && <span>{player.homeTownName}</span>}
              </dd>
            </>
          )}

          {!!industryRating && (
            <>
              <dt>Industry Rating</dt>
              <dd className={styles.rating}>{industryRating}</dd>
            </>
          )}
        </dl>
      </div>
      <div className={styles.industryRatings}>
        <header
          aria-hidden="true"
          className={styles.industryRankingsHeaders}
          role="presentation"
        >
          <div className={styles.headers}>
            <span>NATL</span>
            <span>POS</span>
            <span>ST</span>
          </div>
        </header>

        <ul
          className={styles.industryRatingsList}
          data-rivals-industry={isRivalsIndustry}
        >
          {playerRatings.map((r, index) => {
            if (isRivalsIndustry && r.type === 'Rivals') {
              return null;
            }

            const isIndustry = r.type === 'Industry';
            const isOn3 = r.type === 'On3';

            const link = isIndustry
              ? createUrl(['rivals', player.slug, 'industry-comparison'])
              : isOn3
                ? createUrl(['rivals', player.slug, 'recruiting'])
                : r.link;

            const labelledBy = isIndustry ? 'on3-industry' : slugify(r.type);
            const logoType = r.type === 'Industry' ? 'on3industry' : r.type;

            if (!showRatings && !isIndustry) return null;

            return (
              <li
                aria-labelledby={labelledBy}
                className={styles.industryItem}
                data-active={selected === r.type}
                data-service-name={r.type}
                key={`${player.key}-${index}`}
                role="cell"
              >
                <div className={styles.serviceLogo}>
                  <ServiceLogo type={logoType as ServiceType} />
                </div>

                <StarRating
                  fiveStarPlus={r.fiveStarPlus && isIndustry}
                  href={link || ''}
                  rating={r.rating}
                  sport={r.sport}
                  stars={r.stars}
                  type={r.rankingType}
                  year={r.rankingYear}
                />
                <StarRanking
                  className={styles.industryRanking}
                  nationalRank={r.overallRank}
                  positionAbbr={r.positionAbbr}
                  positionRank={r.positionRank}
                  returnEmpty
                  sport={r.sport}
                  stateAbbr={r.stateAbbr}
                  stateRank={r.stateRank}
                  useSiteColor={false}
                  year={r.rankingYear}
                />
              </li>
            );
          })}
          {!showRatings && (
            <li className={styles.premium}>
              <PremiumBlock
                className={styles.membersOnly}
                utmCampaign={utm_campaign}
                utmContent="lock"
                utmMedium="web"
                utmSource="transfer_portal"
              />
            </li>
          )}
        </ul>
      </div>
      <div className={styles.statusContainer} role="cell">
        {player.commitStatus && (
          <TransferStatus
            {...player.commitStatus}
            lastTeam={player.lastTeam}
            predictions={player.predictions}
            recruitmentKey={player.recruitmentKey || 0}
            slug={player.slug}
            year={relatedModel?.year}
          />
        )}
      </div>
    </li>
  );
};
